import React, { Suspense, useDeferredValue, useEffect, useState } from 'react';
import { User, UserGroup, Venue } from '@/types';
import { Plus, Loader2, Search, ShieldAlert, Trash2, X } from 'lucide-react';
import { showAlertModal } from '@shared/components/common/AlertModal';
import { normalizeErrorMessage } from '@/lib/errorUtils';
import { useUserMutations, DataErrorBoundary } from '@features/admin/data';
import { UserModal } from './UserModal';
import { StaffList } from './StaffList';

interface StaffTabProps {
  userGroups: UserGroup[];
  venues: Venue[];
  currentUserGroup: UserGroup | undefined;
  canEdit: boolean;
}

export const StaffTab: React.FC<StaffTabProps> = ({
  userGroups,
  venues,
  currentUserGroup,
  canEdit
}) => {
  const userMutations = useUserMutations();
  const [loadedUsers, setLoadedUsers] = useState<User[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState('');
  const deferredSearch = useDeferredValue(search);

  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [viewingSignature, setViewingSignature] = useState<string | null>(null);
  const [deletingUser, setDeletingUser] = useState<User | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    setPage(1);
  }, [search]);

  const handleDeleteConfirm = async () => {
    if (!deletingUser) return;
    setIsDeleting(true);
    try {
      await userMutations.remove(deletingUser.id);
      setDeletingUser(null);
    } catch (err: unknown) {
      showAlertModal(normalizeErrorMessage(err, 'Failed to delete user.'));
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="animate-fade-in">
      {viewingSignature && (
        <div
          className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4"
          onClick={() => setViewingSignature(null)}
        >
          <div
            className="bg-white p-6 rounded-xl shadow-2xl max-w-md w-full relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setViewingSignature(null)}
              className="absolute top-3 right-3 text-slate-400 hover:text-slate-600"
            >
              <X className="w-5 h-5" />
            </button>
            <h3 className="font-bold text-lg mb-4 text-slate-800">Technician Signature</h3>
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 flex justify-center items-center h-40">
              <img
                src={viewingSignature}
                alt="User Signature"
                className="max-h-full max-w-full object-contain"
              />
            </div>
          </div>
        </div>
      )}

      {editingUser && currentUserGroup && (
        <UserModal
          user={editingUser}
          groups={userGroups}
          venues={venues}
          currentUserGroup={currentUserGroup}
          isExisting={loadedUsers.some((u) => u.id === editingUser.id)}
          onClose={() => setEditingUser(null)}
          onSaved={() => setEditingUser(null)}
        />
      )}

      {deletingUser && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[70] p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full overflow-hidden animate-scale-up border border-slate-200">
            <div className="p-8 text-center">
              <div className="bg-red-50 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                <Trash2 className="w-10 h-10 text-red-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Revoke Staff Access?</h3>
              <p className="text-slate-500 text-xs mb-4 leading-relaxed">
                Are you sure you want to remove <strong>{deletingUser.name}</strong>?<br />
                <span className="text-[10px] font-mono bg-slate-100 px-1 rounded">
                  Record ID: {deletingUser.id}
                </span>
              </p>
              <div className="flex flex-col gap-3">
                <button
                  disabled={isDeleting}
                  onClick={handleDeleteConfirm}
                  className="w-full bg-red-600 text-white py-3 rounded-2xl font-bold hover:bg-red-700 transition-all shadow-lg shadow-red-600/20 flex items-center justify-center gap-2"
                >
                  {isDeleting ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4" />
                  )}
                  {isDeleting ? 'Deleting...' : 'Yes, Delete Member'}
                </button>
                <button
                  disabled={isDeleting}
                  onClick={() => setDeletingUser(null)}
                  className="w-full bg-slate-100 text-slate-600 py-3 rounded-2xl font-bold hover:bg-slate-200 transition-all disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="flex justify-between items-center mb-6">
        <div>
          <h3 className="text-xl font-bold text-slate-800">Staff Management</h3>
          <p className="text-xs text-slate-500 mt-1">
            Configure internal users, roles, and site permissions.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-2.5 text-slate-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search staff..."
              className="pl-9 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 outline-none w-56 bg-white text-sm"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          {canEdit && (
            <button
              onClick={() =>
                setEditingUser({
                  id: '',
                  name: '',
                  email: '',
                  role: 'staff',
                  userGroupId: '',
                  assignedVenueIds: []
                })
              }
              className="bg-slate-900 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-slate-800 shadow-sm transition-all active:scale-95"
            >
              <Plus className="w-4 h-4" /> Add Staff Member
            </button>
          )}
        </div>
      </div>

      {loadedUsers.some((u, i) => loadedUsers.findIndex((o) => o.email === u.email) !== i) && (
        <div className="mb-6 bg-amber-50 border border-amber-100 p-4 rounded-xl flex items-start gap-4">
          <ShieldAlert className="w-6 h-6 text-amber-600 shrink-0" />
          <div>
            <p className="text-sm font-bold text-amber-900">Duplicate Profiles Detected</p>
            <p className="text-xs text-amber-700 mt-1 leading-relaxed">
              Multiple records found with the same email. This usually happens after migrating from
              Demo data to Auth. Please delete the <strong>"Demo"</strong> records (Short IDs) to
              clean up your dashboard.
            </p>
          </div>
        </div>
      )}

      <DataErrorBoundary invalidateOnRetry="users">
        <Suspense
          fallback={
            <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-slate-500 shadow-sm">
              <Loader2 className="w-5 h-5 animate-spin inline-block mr-2" />
              Loading staff users...
            </div>
          }
        >
          <StaffList
            page={page}
            pageSize={pageSize}
            search={deferredSearch}
            userGroups={userGroups}
            currentUserGroup={currentUserGroup}
            canEdit={canEdit}
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(1);
            }}
            onEdit={setEditingUser}
            onDelete={setDeletingUser}
            onViewSignature={setViewingSignature}
            onItemsLoaded={setLoadedUsers}
          />
        </Suspense>
      </DataErrorBoundary>
    </div>
  );
};
